import React, { useEffect } from 'react';
import { View, Image, TouchableOpacity, ScrollView, Platform } from 'react-native';
import csv from 'csvtojson';
import { Table, TableWrapper, Cell } from 'react-native-table-component';
import { setIndiGrainSelectedColumns } from 'src/redux/actions/appAction';
import { Box, Button, HStack, IconButton, Text, Checkbox, Spinner, Center, useColorModeValue } from 'native-base';
import { S3_BUCKET_URL } from '../../constants'
import ParticleImage from '../ParticleImage';
import { containerStyle } from 'src/styles/containerStyle';
import { imageStyle } from 'src/styles/imageStyle';
import { tableStyle } from 'src/styles/tableStyle';
import { Modal } from 'native-base';
import MaterialIconByName from '../MaterialIconByName';
import { useSelector, useDispatch } from 'react-redux';
import { IStore } from 'src/redux/reducers';
import { isElectron } from 'src/utils/miscUtils';
import { getText } from 'src/utils/i18n';
import { localizeResultCell, localizeResultLabel, normalizeResultLabel } from 'src/utils/resultLocalization';
import { isAllowedIndiGrainColumn, isLightboxFeatureEnabled, isRiceLikeGrain } from 'src/utils/grainFeatureUtils';

interface FilterItem {
  label: string;
  value: number;
  checked: boolean;
}

interface DisplayIndiGrainResultsTableProps {
  jobId: string | number;
  csvFileUrl: string;
  numItemsPerPage: number;
  productType?: string;
  rejectionCalculationConfig?: {
    rejectedGrains?: {
      selectedKeys?: string[];
      selectedLabels?: string[];
    };
  };
  rejectionMapping?: Record<string, string>;
}

const areArraysEqual = (left: number[], right: number[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const getAvailableColumnIndices = (tableHead: string[], isRice: boolean) =>
  tableHead.reduce<number[]>((indices, label, index) => {
    if (isAllowedIndiGrainColumn(label, isRice)) {
      indices.push(index);
    }

    return indices;
  }, []);

const getRequiredColumnIndices = (tableHead: string[]) =>
  tableHead.reduce<number[]>((indices, label, index) => {
    const normalizedLabel = normalizeResultLabel(label);
    if (normalizedLabel === 'resultLabelSerial' || normalizedLabel === 'resultLabelImage') {
      indices.push(index);
    }

    return indices;
  }, []);

const getDefaultSelectedColumns = (tableHead: string[], availableColumnIndices: number[], isRice: boolean) => {
  const defaultVisibleColumnKeys = isRice
    ? ['resultLabelSerial', 'resultLabelImage', 'grainLength', 'grainWidth']
    : ['resultLabelSerial', 'resultLabelImage', 'grainLength', 'grainArea'];
  const selected = new Set<number>();

  defaultVisibleColumnKeys.forEach((labelKey) => {
    const index = tableHead.findIndex((label) => normalizeResultLabel(label) === labelKey);
    if (index >= 0 && availableColumnIndices.includes(index)) {
      selected.add(index);
    }
  });

  availableColumnIndices.forEach((index) => {
    if (selected.size < 4) {
      selected.add(index);
    }
  });

  return Array.from(selected);
};

export default function DisplayIndiGrainResultsTable(props: DisplayIndiGrainResultsTableProps) {

  const colorMode = useSelector((store: IStore) => store.settings.colorMode);
  const locale = useSelector((store: IStore) => store.app.locale);
  const grains = useSelector((store: IStore) => store.user.userProfile.response?.grains);
  const userProfileSettings = useSelector((store: IStore) => store.user.userProfile.response?.settings);
  const lightboxEnabled = isLightboxFeatureEnabled(userProfileSettings);
  const selectedGrainId = useSelector((store: IStore) => store.job.jobStatus.job?.grainId || store.grainType.grainId);
  const tableHeadBg = useColorModeValue('#f1f8ff', '#2d3748');
  const tableBorderColor = useColorModeValue('#c8e1ff', '#4a5568');

  const [filterModalVisible, setFilterModalVisible] = React.useState(false);

  const showFilterModal = () => {
    setTempFilters(filters);
    setFilterModalVisible(true);
  };
  const hideFilterModal = () => {
    setTempFilters(filters);
    setFilterModalVisible(false);
  };

  const [imageModalVisible, setImageModalVisible] = React.useState(false);

  const [resultCsvData, setResultCsvData] = React.useState<any[][] | undefined>(undefined);

  const showImageModal = () => setImageModalVisible(true);
  const hideImageModal = () => setImageModalVisible(false);

  const [sortByColumn, setSortByColumn] = React.useState("Serial");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");

  const [selectedImageUrl, setSelectedImageUrl] = React.useState("");

  const [filters, setFilters] = React.useState<FilterItem[]>([]);
  const selectedColumns = useSelector((store: IStore) => store.app.indiGrainSelectedColumns) ?? [0, 1, 2, 3];
  const [jobId, setJobId] = React.useState<string | number>(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [page, setPage] = React.useState(0);
  const [numberOfItemsPerPage, setNumberOfItemsPerPage] = React.useState(10);
  const dispatch = useDispatch();
  const [tableHead, setTableHead] = React.useState<any[]>([]);
  const [tableData, setTableData] = React.useState<any[][]>([]);
  const [currentPageData, setCurrentPageData] = React.useState<any[][]>([]);
  const [tempFilters, setTempFilters] = React.useState<FilterItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const grainName = React.useMemo(
    () => grains?.find((grain: { grainId?: string; name?: string }) => grain.grainId?.toString() === selectedGrainId?.toString())?.name,
    [grains, selectedGrainId]
  );
  const isRice = React.useMemo(
    () => isRiceLikeGrain(selectedGrainId, grainName),
    [selectedGrainId, grainName]
  );
  const visibleColumnIndices = React.useMemo(
    () => getAvailableColumnIndices(tableHead, isRice),
    [tableHead, isRice]
  );
  const requiredColumnIndices = React.useMemo(
    () => getRequiredColumnIndices(tableHead),
    [tableHead]
  );
  const selectableColumnIndices = React.useMemo(
    () => visibleColumnIndices.filter((index) => !requiredColumnIndices.includes(index)),
    [requiredColumnIndices, visibleColumnIndices]
  );
  const effectiveSelectedColumns = React.useMemo(() => {
    const sanitizedColumns = selectedColumns.filter((index) => visibleColumnIndices.includes(index));
    if (sanitizedColumns.length > 0) {
      return sanitizedColumns;
    }

    return getDefaultSelectedColumns(tableHead, visibleColumnIndices, isRice);
  }, [selectedColumns, tableHead, visibleColumnIndices, isRice]);
  const legacySelectedColumns = React.useMemo(() => {
    const sanitizedColumns = selectedColumns.filter((index) => index >= 0 && index < tableHead.length);
    if (sanitizedColumns.length > 0) {
      return sanitizedColumns;
    }

    return [0, 1, 2, 3].filter((index) => index < tableHead.length);
  }, [selectedColumns, tableHead.length]);
  const activeSelectedColumns = lightboxEnabled ? effectiveSelectedColumns : legacySelectedColumns;
  useEffect(() => {
    if (filters.length === 0) {
      return;
    }

    const hasChanges = filters.some((filter) =>
      filter.checked !== activeSelectedColumns.includes(filter.value)
    );

    if (hasChanges) {
      const updatedFilters = filters.map((filter) => ({
        ...filter,
        checked: activeSelectedColumns.includes(filter.value)
      }));
      setFilters(updatedFilters);
    }
  }, [activeSelectedColumns, filters]);

  useEffect(() => {
    if (!lightboxEnabled || tableHead.length === 0) {
      return;
    }

    const nextFilters = tableHead
      .map((label, index) => ({
        label,
        value: index,
        checked: effectiveSelectedColumns.includes(index)
      }))
      .filter((item) => selectableColumnIndices.includes(item.value));

    setFilters(nextFilters);
    setTempFilters(nextFilters);
  }, [effectiveSelectedColumns, lightboxEnabled, selectableColumnIndices, tableHead]);

  useEffect(() => {
    if (!lightboxEnabled || tableHead.length === 0) {
      return;
    }

    if (!areArraysEqual(selectedColumns, effectiveSelectedColumns)) {
      dispatch(setIndiGrainSelectedColumns(effectiveSelectedColumns));
    }
  }, [dispatch, effectiveSelectedColumns, lightboxEnabled, selectedColumns, tableHead]);

  useEffect(() => {
    setJobId(props.jobId);
    setInitialTableData(props.csvFileUrl);
    setNumberOfItemsPerPage(props.numItemsPerPage);
  }, [props.jobId, props.csvFileUrl, props.numItemsPerPage]);

  useEffect(() => {
    updateCurrentPageData();
  }, [page]);

  const sortItems = (column: string) => {
    if (sortByColumn === column) {
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
      setSortOrder(newSortOrder)
      if (resultCsvData !== undefined) {
        updateTableData(resultCsvData, column, newSortOrder)
      }

    } else {
      setSortByColumn(column)
      setSortOrder("asc")
      if (resultCsvData !== undefined) {
        updateTableData(resultCsvData, column, 'asc')
      }
    }
  }

  const updateTableData = (data: any[][], sortBy: string, sortingOrder: string) => {
    const head = [...data[0]];
    const rows = data.slice(1).map((item) => [...item]);

    if (!head.includes('Serial')) {
      head.splice(0, 0, 'Serial');

      let idx = 1;
      rows.forEach((item: any) => {
        item.splice(0, 0, idx);
        idx += 1;
      });
    }

    if (lightboxEnabled) {
      setTableHead(head);
      const sortedData = getSortedData(head, rows, sortBy, sortingOrder);
      setTableData(sortedData);
      return;
    }

    setTableHead(head);
    const sortedData = getSortedData(head, rows, sortBy, sortingOrder);

    const rowFilters: FilterItem[] = [];
    head.forEach((item: any, index: number) => {
      rowFilters.push({
        label: item,
        value: index,
        checked: legacySelectedColumns.includes(index)
      });
    });

    setFilters(rowFilters);
    setTempFilters(rowFilters);
    setTableData(sortedData);
  };

  const getSortedData = (head: any, rows: any, sortBy: string, sortingOrder: string) => {
    const columnIndex = head.indexOf(sortBy);
    if (columnIndex < 0) {
      return [...rows];
    }

    const sortedData = [...rows].sort((a: any, b: any) => {
      if (sortingOrder === 'asc') {
        if (a[columnIndex] === 'True' || a[columnIndex] === 'False') {
          return a[columnIndex].localeCompare(b[columnIndex]);
        }
        return a[columnIndex] - b[columnIndex];
      } else {
        if (a[columnIndex] === 'True' || a[columnIndex] === 'False') {
          return b[columnIndex].localeCompare(a[columnIndex]);
        }
        return b[columnIndex] - a[columnIndex];
      }
    });
    return sortedData;
  };

  useEffect(() => {
    updateCurrentPageData();
  }, [tableData]);

  useEffect(() => {
    if (!lightboxEnabled && resultCsvData !== undefined) {
      updateTableData(resultCsvData, sortByColumn, sortOrder);
    }
  }, [lightboxEnabled, locale, resultCsvData, sortByColumn, sortOrder]);

  const updateCurrentPageData = () => {
    const startIndex = page * numberOfItemsPerPage;
    let endIndex = startIndex + numberOfItemsPerPage;
    if (endIndex > tableData.length) {
      endIndex = tableData.length;
    }
    setCurrentPageData(tableData.slice(startIndex, endIndex));
  };

  const setInitialTableData = async (csvFileUrl: string) => {
    setIsLoading(true);
    try {
      let csvText: string;
      if (isElectron() && typeof window !== 'undefined' && (window as any).electron?.fetchFileContent) {
        csvText = await (window as any).electron.fetchFileContent(csvFileUrl);
      } else if (csvFileUrl.startsWith('file://')) {
        if (!isElectron() || typeof window === 'undefined' || !(window as any).electron?.readLocalFile) {
          throw new Error('Local file access not available');
        }
        csvText = await (window as any).electron.readLocalFile(csvFileUrl);
      } else {
        const response = await fetch(csvFileUrl);
        csvText = await response.text();
      }

      const csvRow = await csv({
        noheader: true,
        output: "csv"
      }).fromString(csvText);

      const head = csvRow[0];
      let newCsvRow = csvRow.map((item) => [...item]);
      if (head.includes('Volumn')) {
        const index = head.indexOf('Volumn');
        if (index > -1) {
          newCsvRow = csvRow.map((item) => {
            const nextItem = [...item];
            nextItem.splice(index, 1);
            return nextItem;
          });
        }
      }

      setResultCsvData(newCsvRow);
      setTotalPages(Math.max(1, Math.ceil(Math.max(newCsvRow.length - 1, 0) / numberOfItemsPerPage)));
      setPage(0);
      updateTableData(newCsvRow, sortByColumn, sortOrder);
    } catch (error) {
      console.error("some error occurred", error);
    } finally {
      setIsLoading(false);
    }
  };

  const imageElement = (data: any) => (
    <TouchableOpacity onPress={() => {
      setSelectedImageUrl(getImageUrl(data))
      showImageModal();
    }} >
      <Image style={props?.productType === "DOUBLE_SIDED_GRAIN_ANALYSIS" ? imageStyle.dsIndiGrainImage : imageStyle.indiGrainImage} source={{ uri: getImageUrl(data) }} />
    </TouchableOpacity>
  );

  const getImageUrl = (imageName: string) => {
    const csvUrl = props.csvFileUrl || '';
    if (csvUrl.startsWith('file://')) {
      const baseUrl = csvUrl.substring(0, csvUrl.lastIndexOf('/'));
      return `${baseUrl}/simgPy/${imageName}`;
    }

    return `${S3_BUCKET_URL()}/output/${jobId}/simgPy/${imageName}`;
  };

  const headElement = (data: any) => (
    <TouchableOpacity onPress={() => {
      sortItems(data)
    }} >
      <HStack space={1} alignItems="center">
        <Text style={[tableStyle.cellText, {
          color: colorMode === 'dark' ? 'white' : 'black'
        }]}>{localizeResultLabel(data, { locale })}</Text>
        {sortByColumn === data && (
          <Text style={[tableStyle.cellText, {
            color: colorMode === 'dark' ? 'white' : 'black'
          }]}>{sortOrder === 'asc' ? '↑' : '↓'}</Text>
        )}
      </HStack>
    </TouchableOpacity>
  );

  const formatNumericCell = (value: unknown): string => {
    if (value === null || value === undefined) return '';

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return String(value);
      return Number.isInteger(value) ? String(value) : value.toFixed(2);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      if (trimmed === 'True' || trimmed === 'False') return trimmed;

      if (!/[.eE]/.test(trimmed)) return value;
      if (!/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmed)) return value;

      const asNumber = Number(trimmed);
      if (!Number.isFinite(asNumber)) return value;
      return Number.isInteger(asNumber) ? String(asNumber) : asNumber.toFixed(2);
    }

    return String(value);
  };

  const textElement = (data: any) => (
    <Text style={[tableStyle.cellText, {
      color: colorMode === 'dark' ? 'white' : 'black'
    }]}>{localizeResultCell(formatNumericCell(data), { locale })}</Text>
  );

  const onItemCheckChange = (item: FilterItem) => {
    setTempFilters((prevFilters) => prevFilters.map((filter) => (
      filter.value === item.value
        ? { ...filter, checked: !filter.checked }
        : { ...filter }
    )));
  };

  const applyFilters = () => {
    if (lightboxEnabled) {
      const checkedOptionalColumns = new Set(
        tempFilters
          .filter((filter) => filter.checked)
          .map((filter) => filter.value)
      );
      const newCols = visibleColumnIndices.filter((index) => (
        requiredColumnIndices.includes(index) || checkedOptionalColumns.has(index)
      ));

      dispatch(setIndiGrainSelectedColumns(newCols));
      hideFilterModal();
      return;
    }

    setFilters(tempFilters);
    const newCols = tempFilters
      .filter((filter) => filter.checked)
      .map((filter) => filter.value);

    dispatch(setIndiGrainSelectedColumns(newCols));
    hideFilterModal();
  };

  const resetFilters = () => {
    if (lightboxEnabled) {
      const defaultColumns = getDefaultSelectedColumns(tableHead, visibleColumnIndices, isRice);
      const resetItems = tempFilters.map((filter) => ({
        ...filter,
        checked: defaultColumns.includes(filter.value)
      }));
      setTempFilters(resetItems);
      return;
    }

    const resetItems = tempFilters.map((filter) => ({
      ...filter,
      checked: filter.value === 0 || filter.value === 1 || filter.value === 2 || filter.value === 3
    }));
    setTempFilters(resetItems);
  };

  const isImageColumn = (columnIndex: number) =>
    normalizeResultLabel(tableHead[columnIndex]) === 'resultLabelImage';

  const initialRefFilterModal = React.useRef(null);
  const finalRefFilterModal = React.useRef(null);

  const initialRefImageModal = React.useRef(null);
  const finalRefImageModal = React.useRef(null);

  return (
    <View style={containerStyle.mainContainer}>
      <Modal
        size={"lg"}
        isOpen={filterModalVisible}
        onClose={hideFilterModal}
        initialFocusRef={initialRefFilterModal}
        finalFocusRef={finalRefFilterModal}>
        <Modal.Content>
          <Modal.CloseButton />
          <Modal.Header>
            <HStack justifyContent="space-between" alignItems="center" pr={8}>
              <Text fontSize="lg" fontWeight="bold">{getText('selectColumns')}</Text>
              <Button
                size="sm"
                variant="ghost"
                onPress={resetFilters}
              >
                {getText('reset')}
              </Button>
            </HStack>
          </Modal.Header>
          <Modal.Body>
            <ScrollView
              style={{ maxHeight: 400 }}
              contentContainerStyle={{ paddingBottom: 10 }}
              nestedScrollEnabled={true}
              showsVerticalScrollIndicator={true}
              {...(Platform.OS === 'android' ? {
                removeClippedSubviews: false,
                keyboardShouldPersistTaps: 'handled'
              } : {})}
            >
              {tempFilters.map((item) => (
                <Box
                  key={`filter-${item.value}-${item.label}`}
                  py={2}
                  borderBottomWidth={1}
                  borderBottomColor={colorMode === 'dark' ? 'gray.700' : 'gray.200'}
                >
                  <Checkbox
                    isDisabled={!lightboxEnabled && (item.value === 0 || item.value === 1)}
                    value={item.label}
                    isChecked={item.checked}
                    onChange={() => onItemCheckChange(item)}
                    accessibilityLabel={`Toggle ${localizeResultLabel(item.label, { locale })} column`}
                  >
                    <Text>{localizeResultLabel(item.label, { locale })}</Text>
                  </Checkbox>
                </Box>
              ))}
            </ScrollView>
            <Text fontSize="xs" color="gray.500" mt={2}>
              {getText('serialImageColumnsRequired')}
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button.Group space={2}>
              <Button variant="ghost" colorScheme="blueGray" onPress={hideFilterModal}>
                {getText('cancel')}
              </Button>
              <Button onPress={applyFilters}>{getText('apply')}</Button>
            </Button.Group>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
      <Modal
        size={"lg"}
        isOpen={imageModalVisible}
        onClose={hideImageModal}
        initialFocusRef={initialRefImageModal}
        finalFocusRef={finalRefImageModal}>
        <Modal.Content>
          <Modal.CloseButton />
          <Modal.Header>{getText('resultLabelImage')}</Modal.Header>
          <Modal.Body>
            <View style={{ flexDirection: 'column', flexWrap: 'wrap' }}>
              {
                selectedImageUrl !== "" ? (<>
                  <TouchableOpacity style={{ height: "100%", width: "100%" }}>
                    <View style={{ height: "100%", width: "100%", justifyContent: 'space-around' }}>
                      <ParticleImage
                        labeled_image={selectedImageUrl}
                        width={props?.productType === "DOUBLE_SIDED_GRAIN_ANALYSIS" ? 800 : 600}
                        height={props?.productType === "DOUBLE_SIDED_GRAIN_ANALYSIS" ? 400 : 600}
                      />
                    </View>
                  </TouchableOpacity>
                </>) : (<>
                  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Text>{getText('nothingToDisplay')}</Text>
                  </View>
                </>)
              }
            </View>
          </Modal.Body>
        </Modal.Content>
      </Modal>
      <HStack justifyContent="space-between" alignItems="center" mb={3}>
        <Text fontSize="md" fontWeight="medium">
          {getText('showingColumns', {
            selected: activeSelectedColumns.length,
            total: lightboxEnabled ? visibleColumnIndices.length : tableHead.length
          })}
        </Text>
        <Button
          size="sm"
          leftIcon={<MaterialIconByName name="filter" />}
          onPress={showFilterModal}
        >
          {getText('columns')}
        </Button>
      </HStack>
      {isLoading ? (
        <Center p={8}>
          <Spinner size="lg" />
          <Text mt={2}>{getText('loadingData')}</Text>
        </Center>
      ) : tableData.length === 0 ? (
        <Center p={8}>
          <Text>{getText('noDataAvailable')}</Text>
        </Center>
      ) : (
        <>
          <Table borderStyle={{ borderWidth: 2, borderColor: tableBorderColor }}>
            <TableWrapper style={[tableStyle.head, { backgroundColor: tableHeadBg }]}>
              {
                activeSelectedColumns.map((columnIndex) => (
                  <Cell key={columnIndex} data={headElement(tableHead[columnIndex])} />
                )) as any
              }
            </TableWrapper>
            {
              currentPageData.map((rowData, index) => (
                <TableWrapper key={index} style={tableStyle.row}>
                  {
                    activeSelectedColumns.map((columnIndex) => (
                      <Cell
                        key={columnIndex}
                        data={isImageColumn(columnIndex) ? imageElement(rowData[columnIndex]) : textElement(rowData[columnIndex])}
                        textStyle={tableStyle.cellText}
                      />
                    )) as any
                  }
                </TableWrapper>
              )) as any
            }
          </Table>
          <Box>
            <HStack space={2}
              style={{
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}>
              <Text>
                Page {page + 1} of {totalPages}
              </Text>
              <IconButton
                onPress={() => {
                  if (page > 0) {
                    setPage(page - 1)
                  }
                }}
                isDisabled={page === 0}
                borderRadius={"full"}
                icon={<MaterialIconByName name={"chevron-left"} />} />
              <IconButton
                borderRadius={"full"}
                onPress={() => {
                  if (page < totalPages - 1) {
                    setPage(page + 1)
                  }
                }}
                isDisabled={page >= totalPages - 1}
                icon={<MaterialIconByName name={"chevron-right"} />} />
            </HStack>
          </Box>
        </>
      )}
    </View>
  );
}
